import { TOG_CsrDay7 } from '@/rtk-query.gql';

import { AccountSummary, MatingDetail, Totals } from './types';

const createEmptyAccountSummary = (account: string): AccountSummary => ({
  account,
  isHeader: true,
  frozen: 0,
  implanted: 0,
  d65Frozen: 0,
  discarded: 0,
  total: 0,
});

const createEmptyMatingDetail = (row: TOG_CsrDay7): MatingDetail => ({
  mating: row.donor || '',
  sire: row.sire || '',
  bullBreed: row.bullBreed || '',
  breed: row.breed || '',
  frozen: 0,
  implanted: 0,
  d65Frozen: 0,
  discarded: 0,
  total: 0,
});

const calculateTotal = (summary: Omit<Totals, 'total'>): number =>
  summary.frozen + summary.implanted + summary.d65Frozen + summary.discarded;

const updateCounts = (
  summary: Omit<Totals, 'total'>,
  embryoToBe: string | null | undefined,
): void => {
  if (embryoToBe === 'Frozen') summary.frozen++;
  else if (embryoToBe === 'Implanted') summary.implanted++;
  else if (embryoToBe === 'D6.5-Froz' || embryoToBe === 'D6.5-Frozen')
    summary.d65Frozen++;
  else if (embryoToBe === 'Discarded') summary.discarded++;
};

export const TransformDataToSummary = (
  csrDay7Data: TOG_CsrDay7[],
): {
  summaryData: AccountSummary[];
  totals: Totals;
} => {
  const accountMap = new Map<string, AccountSummary>();
  const matingMap = new Map<string, Map<string, MatingDetail>>();

  csrDay7Data.forEach((row) => {
    const account = row.account || '';
    if (!accountMap.has(account)) {
      accountMap.set(account, createEmptyAccountSummary(account));
    }

    if (!matingMap.has(account)) {
      matingMap.set(account, new Map<string, MatingDetail>());
    }

    const matingKey = `${row.donor || ''}_${row.sire || ''}_${row.bullBreed || ''}`;
    const accountMatingMap = matingMap.get(account)!;

    if (!accountMatingMap.has(matingKey)) {
      accountMatingMap.set(matingKey, createEmptyMatingDetail(row));
    }

    const accountSummary = accountMap.get(account)!;
    const matingDetail = accountMatingMap.get(matingKey)!;

    updateCounts(accountSummary, row.embryoToBe);
    updateCounts(matingDetail, row.embryoToBe);
  });

  const summaryData: AccountSummary[] = [];

  accountMap.forEach((accountSummary, account) => {
    accountSummary.total = calculateTotal(accountSummary);

    summaryData.push(accountSummary);

    const accountMatingMap = matingMap.get(account)!;

    accountMatingMap.forEach((matingDetail) => {
      matingDetail.total = calculateTotal(matingDetail);
      summaryData.push({
        account,
        isHeader: false,
        mating: matingDetail.mating,
        sire: matingDetail.sire,
        breed: matingDetail.breed,
        bullBreed: matingDetail.bullBreed,
        frozen: matingDetail.frozen,
        implanted: matingDetail.implanted,
        d65Frozen: matingDetail.d65Frozen,
        discarded: matingDetail.discarded,
        total: matingDetail.total,
      });
    });
  });

  const totals = summaryData
    .filter((row) => row.isHeader)
    .reduce(
      (acc, curr) => ({
        frozen: acc.frozen + curr.frozen,
        implanted: acc.implanted + curr.implanted,
        d65Frozen: acc.d65Frozen + curr.d65Frozen,
        discarded: acc.discarded + curr.discarded,
        total: acc.total + curr.total,
      }),
      { frozen: 0, implanted: 0, d65Frozen: 0, discarded: 0, total: 0 },
    );

  return { summaryData, totals };
};
